import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plane, Ship, ArrowRight, Boxes, Home, Truck, User, Plus, Trash2, AlertTriangle, ShoppingBag, MapPin, Search, PackagePlus, MessageCircle, Download } from "lucide-react";
import { downloadPdf } from "@/lib/download";
import { MyPurchases, MyAddresses } from "@/components/ShopShipTabs";
import { PickupBox } from "@/components/PickupBox";
import { clientShipStatus } from "@/lib/clientStatus";
import { SecurityPanel } from "@/components/SecurityPanel";
import { useFeatures } from "@/context/FeaturesContext";
import { curSym, shipCur } from "@/lib/currency";

export const STATUS_LABEL = {
  created: "st.created", receiving: "st.receiving", consolidated: "st.consolidated", paid_pending: "st.paid_pending",
  paid: "st.paid", shipped: "st.shipped", in_transit: "st.inTransit", arrived: "st.arrived", available: "st.available", delivered: "st.delivered",
};
export const STATUS_COLOR = {
  created: "bg-secondary text-primary", receiving: "bg-blue-100 text-blue-700", consolidated: "bg-amber-100 text-amber-700",
  paid_pending: "bg-purple-100 text-purple-700", paid: "bg-emerald-100 text-emerald-700", shipped: "bg-accent/20 text-accent",
  arrived: "bg-emerald-100 text-emerald-700", delivered: "bg-emerald-600 text-white",
};
const DISPUTE_TYPES = {
  missing: "Colis manquant", weight: "Poids contesté", cbm: "CBM contesté", damaged: "Colis endommagé",
  price: "Prix différent", delay: "Retard", delivery: "Problème de livraison", other: "Autre",
};
const ROUTE_ST = ["shipped", "in_transit", "arrived", "available"];
const wa = (text) => `https://wa.me/447466398452?text=${encodeURIComponent(text)}`;
const curOf = (s) => curSym(shipCur(s));

// Bouton d'annulation client : affiche les éventuels frais AVANT confirmation (politique configurée par l'admin)
export const CancelAction = ({ kind, id, refLabel, onDone, t }) => {
  const [prev, setPrev] = useState(null);
  const [busy, setBusy] = useState(false);
  const open = async () => {
    try { const { data } = await api.get(`/cancellation/preview?kind=${kind}&id=${id}`); setPrev(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/cancellation/confirm", { kind, id });
      toast.success(data.fee > 0
        ? `${t("cx.done")} — ${t("cx.fee")} : ${data.fee} ${curSym(data.currency)}`
        : t("cx.done"));
      setPrev(null); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };
  return (
    <>
      <Button size="sm" variant="outline" className="rounded-sm text-xs text-red-600 border-red-200"
        data-testid={`cancel-btn-${id}`} onClick={open}>{t("cx.cancel")}</Button>
      <Dialog open={!!prev} onOpenChange={(v) => !v && setPrev(null)}>
        <DialogContent className="rounded-sm" data-testid="cancel-dialog">
          <DialogHeader><DialogTitle>{t("cx.title")} — {refLabel}</DialogTitle></DialogHeader>
          {prev && !prev.allowed && <p className="text-sm text-red-700" data-testid="cancel-blocked">{prev.reason}</p>}
          {prev && prev.allowed && (
            <>
              <p className="text-sm text-muted-foreground" data-testid="cancel-message">{prev.message}</p>
              {prev.fee > 0 && (
                <div className="border border-amber-300 bg-amber-50 p-3 text-sm font-semibold" data-testid="cancel-fee">
                  {t("cx.fee")} : {prev.fee} {curSym(prev.currency)}
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" className="rounded-sm" onClick={() => setPrev(null)}>{t("cx.keep")}</Button>
                <Button data-testid="cancel-confirm" disabled={busy} className="rounded-sm bg-red-600 text-white hover:bg-red-700" onClick={confirm}>{t("cx.confirm")}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default function CustomerDashboard() {
  const { t } = useI18n();
  const { user, setUser } = useAuth();
  const { isOn } = useFeatures();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab") || "home";
  // Anciennes rubriques « Mes colis » / « Marchandises » / « Support » redirigées
  const tab = ["goods", "packages", "shipments"].includes(rawTab) ? "shipments" : rawTab === "support" ? "profile" : rawTab;
  const sub = searchParams.get("sub") || "";
  const [shipments, setShipments] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [purchFilter, setPurchFilter] = useState("active");
  // Collecte du numéro WhatsApp — une seule fois après inscription/première connexion
  const [waOpen, setWaOpen] = useState(false);
  const [waNum, setWaNum] = useState("");
  useEffect(() => {
    if (user?.role === "customer" && !user.whatsapp && !localStorage.getItem(`wa_later_${user.user_id}`)) setWaOpen(true);
  }, [user?.user_id, user?.whatsapp, user?.role]);
  const saveWhatsApp = async () => {
    const v = waNum.trim();
    if (!/^\+?[0-9 ]{8,18}$/.test(v)) { toast.error("Numéro invalide — utilisez l'indicatif pays, ex. +44 7XXXXXXXXX"); return; }
    const { data } = await api.put("/me", { whatsapp: v.startsWith("+") ? v : `+${v}` });
    if (setUser) setUser(data);
    setWaOpen(false);
    toast.success("Numéro WhatsApp enregistré");
  };
  const waLater = () => { localStorage.setItem(`wa_later_${user?.user_id}`, "1"); setWaOpen(false); };

  const load = () => {
    api.get("/shipments").then(({ data }) => setShipments(data));
    api.get("/disputes").then(({ data }) => setDisputes(data));
    api.get("/shopship/purchases").then(({ data }) => setPurchases(data)).catch(() => {});
    api.get("/me/invoices").then(({ data }) => setInvoices(data)).catch(() => {});
  };
  useEffect(load, []);
  const go = (v, s) => setSearchParams(s ? { tab: v, sub: s } : { tab: v });

  const payFor = async (sid) => {
    try {
      const { data } = await api.post("/payments/checkout", { shipment_id: sid, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de paiement"); }
  };

  // À FAIRE — uniquement les dossiers nécessitant une ACTION du client (jamais de simples statuts)
  const todoOf = (s) => {
    if (["cancelled", "delivered"].includes(s.status)) return null;
    // Devis envoyé par Colis2Go : décision requise — « Voir le devis » ouvre le panneau existant (accepter / annuler)
    if (s.quote_status === "sent" && s.quote?.amount != null)
      return { label: "Devis reçu — décision requise", btn: "Voir le devis", onClick: () => navigate(`/shipments/${s.id}`) };
    if ((s.price_adjustments || []).some((a) => !a.confirmed))
      return { label: "Ajustement de prix à confirmer", btn: "Continuer", onClick: () => navigate(`/shipments/${s.id}`) };
    if (s.final_price != null && !s.final_accepted && !s.prepaid_invoice)
      return { label: "Prix final disponible", btn: "Voir et accepter", onClick: () => navigate(`/shipments/${s.id}`) };
    if (s.final_price != null && !s.paid && (s.prepaid_invoice || s.final_accepted)) {
      const amt = s.balance_due && s.balance_due !== s.final_price ? s.balance_due : s.final_price;
      return {
        label: s.quote_status === "accepted" ? "Devis accepté — paiement requis" : "Paiement requis",
        amount: `${amt} ${curOf(s)}`,
        btn: isOn("payments") ? "Payer maintenant" : "Voir le dossier",
        onClick: () => (isOn("payments") ? payFor(s.id) : navigate(`/shipments/${s.id}`)),
      };
    }
    return null;
  };
  const todos = shipments.map((s) => ({ s, a: todoOf(s) })).filter((x) => x.a);

  // EXPÉDITIONS — « J'ai déjà ma marchandise » + suivi jusqu'à la livraison
  const goodsShips = shipments.filter((s) => s.module !== "shopship");
  // « Sur devis » : devis PAS ENCORE envoyé par Colis2Go — dès l'envoi, le dossier passe dans « À faire »
  const shipQuote = goodsShips.filter((s) => s.final_price == null && s.quote_status !== "sent" && !["delivered", "cancelled"].includes(s.status));
  const pricedShips = goodsShips.filter((s) => !shipQuote.includes(s));
  const shipTodo = pricedShips.filter((s) => todoOf(s));
  const shipDone = goodsShips.filter((s) => ["delivered", "cancelled"].includes(s.status));
  const shipRoute = pricedShips.filter((s) => ROUTE_ST.includes(s.status));
  const shipWaiting = pricedShips.filter((s) => !ROUTE_ST.includes(s.status) && !["delivered", "cancelled"].includes(s.status)
    && ["created", "receiving"].includes(s.status) && (s.packages || []).length === 0 && s.quote_status !== "sent");
  const shipAtHub = pricedShips.filter((s) => !ROUTE_ST.includes(s.status) && !["delivered", "cancelled"].includes(s.status)
    && ((s.packages || []).length > 0 || !["created", "receiving"].includes(s.status)));
  const shipSub = sub || (shipTodo.length ? "todo" : shipQuote.length ? "quote" : "hub");

  const SIDENAV = [
    ["home", "Accueil", Home],
    ...(isOn("shop_and_ship") ? [["purchases", "Achats en ligne", ShoppingBag]] : []),
    ["shipments", "Expéditions", Truck],
  ];
  const SIDENAV2 = [
    ...(isOn("shop_and_ship") ? [["addresses", "Mes adresses Colis2Go", MapPin]] : []),
    ["profile", "Profil", User],
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 md:flex md:gap-8">
        {/* MENU VERTICAL — desktop uniquement (mobile : barre fixe en bas) */}
        <aside className="hidden md:block w-52 shrink-0" data-testid="desktop-sidenav">
          <nav className="sticky top-20 space-y-1">
            {SIDENAV.map(([v, l, Icon]) => (
              <button key={v} data-testid={`side-${v}`} onClick={() => go(v)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm font-semibold text-left transition-colors ${tab === v ? "bg-primary text-white" : "hover:bg-secondary text-primary"}`}>
                <Icon className="h-4 w-4" /> {l}
              </button>
            ))}
            <button data-testid="side-search" onClick={() => navigate("/")}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm font-semibold text-left hover:bg-secondary text-primary transition-colors">
              <Search className="h-4 w-4" /> Rechercher
            </button>
            <div className="pt-3 mt-3 border-t border-border space-y-1">
              {SIDENAV2.map(([v, l, Icon]) => (
                <button key={v} data-testid={`side-${v}`} onClick={() => go(v)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-left transition-colors ${tab === v ? "bg-primary text-white" : "hover:bg-secondary text-muted-foreground"}`}>
                  <Icon className="h-4 w-4" /> {l}
                </button>
              ))}
              <a data-testid="side-whatsapp" href={wa("Bonjour Colis2Go, j'ai besoin d'aide.")} target="_blank" rel="noreferrer"
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-left hover:bg-secondary text-muted-foreground transition-colors">
                <MessageCircle className="h-4 w-4 text-[#25D366]" /> Nous contacter sur WhatsApp
              </a>
            </div>
          </nav>
        </aside>

        <div className="flex-1 min-w-0">
          {tab === "home" && (
            <h1 className="font-heading font-extrabold text-3xl tracking-tight" data-testid="dash-hello">
              {t("dashx.hello").replace("{name}", (user?.name || "").split(" ")[0])}
            </h1>
          )}

          {/* Email = canal principal : rappel si non vérifié */}
          {user && user.email_verified === false && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border border-amber-300 bg-amber-50 p-3" data-testid="verify-email-banner">
              <p className="text-sm font-medium text-amber-800">{t("vf.banner")}</p>
              <Button size="sm" data-testid="verify-email-goto" className="rounded-sm bg-accent text-white h-8" onClick={() => go("profile")}>{t("vf.action")}</Button>
            </div>
          )}

          <Tabs value={tab} className="mt-2">
            {/* COLLECTE WHATSAPP — écran court, une seule fois, jamais bloquant */}
            <Dialog open={waOpen} onOpenChange={(v) => { if (!v) waLater(); }}>
              <DialogContent className="rounded-sm max-w-md" data-testid="whatsapp-prompt">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><MessageCircle className="h-5 w-5 text-[#25D366]" /> Ajoutez votre numéro WhatsApp</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground">
                  Pour recevoir les informations importantes concernant vos colis et permettre à Colis2Go de vous contacter rapidement si nécessaire.
                </p>
                <div>
                  <Label>Numéro WhatsApp</Label>
                  <Input data-testid="whatsapp-input" className="rounded-sm mt-1" placeholder="+44 7XXXXXXXXX" value={waNum} onChange={(e) => setWaNum(e.target.value)} />
                  <p className="mt-1 text-xs text-muted-foreground">Avec l&rsquo;indicatif pays.</p>
                </div>
                <DialogFooter className="gap-2">
                  <Button data-testid="whatsapp-later" variant="outline" className="rounded-sm" onClick={waLater}>Plus tard</Button>
                  <Button data-testid="whatsapp-save" className="rounded-sm bg-accent hover:bg-accent/90 text-white" onClick={saveWhatsApp}>Continuer</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* ACCUEIL — agir ou créer une nouvelle demande */}
            <TabsContent value="home" className="mt-4">
              {/* Rappel WhatsApp — non bloquant, jamais dans « À faire », disparaît une fois le numéro ajouté */}
              {user?.role === "customer" && !user?.whatsapp && !waOpen && (
                <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border border-[#25D366]/40 bg-[#25D366]/5 p-3" data-testid="whatsapp-banner">
                  <div className="text-sm">
                    <div className="font-semibold flex items-center gap-1.5"><MessageCircle className="h-4 w-4 text-[#25D366]" /> Ajoutez votre WhatsApp pour faciliter le suivi de vos colis</div>
                    <div className="text-muted-foreground text-xs mt-0.5">Nous pourrons vous contacter rapidement si une information est nécessaire.</div>
                  </div>
                  <Button size="sm" data-testid="whatsapp-banner-add" className="rounded-sm bg-accent text-white h-8" onClick={() => setWaOpen(true)}>Ajouter mon WhatsApp</Button>
                </div>
              )}
              <div className="md:grid md:grid-cols-3 md:gap-6">
                <div className="md:col-span-2">
                  {/* À FAIRE — uniquement les actions réelles du client */}
                  {todos.length > 0 && (
                    <div className="border-2 border-accent/40 bg-card" data-testid="todo-section">
                      <div className="px-4 pt-3 text-xs font-bold uppercase tracking-wider text-accent">À faire ({todos.length})</div>
                      <div className="mt-2 divide-y divide-border">
                        {todos.slice(0, 3).map(({ s, a }) => (
                          <div key={s.id} data-testid={`todo-${s.reference}`} className="px-4 py-3 flex flex-wrap items-center justify-between gap-2">
                            <div>
                              <div className="font-mono font-bold text-sm">{s.reference}</div>
                              <div className="text-sm text-muted-foreground">{a.label}{a.amount && <b className="ml-2 text-accent">{a.amount}</b>}</div>
                            </div>
                            <Button size="sm" data-testid={`todo-btn-${s.reference}`} className="rounded-sm bg-accent hover:bg-accent/90 text-white" onClick={a.onClick}>{a.btn}</Button>
                          </div>
                        ))}
                      </div>
                      {todos.length > 3 && (
                        <button data-testid="todo-see-all" className="w-full px-4 py-2.5 text-sm font-semibold text-accent hover:bg-secondary border-t border-border"
                          onClick={() => go("shipments", "todo")}>Voir tout ({todos.length})</button>
                      )}
                    </div>
                  )}

                  {/* QUE SOUHAITEZ-VOUS FAIRE ? — créer une NOUVELLE demande */}
                  <h2 className={`font-heading font-extrabold text-xl tracking-tight ${todos.length ? "mt-6" : "mt-1"}`}>{t("dashx.whatDo")}</h2>
                  <div className="mt-3 grid gap-4 sm:grid-cols-2">
                    <button data-testid="big-buy-online" onClick={() => navigate("/shopship")}
                      className="text-left border-2 border-border bg-card p-6 hover:border-accent transition-colors">
                      <ShoppingBag className="h-7 w-7 text-accent" />
                      <div className="mt-2 font-heading font-extrabold text-lg">{t("dashx.buyOnline")}</div>
                      <p className="mt-1 text-sm text-muted-foreground">{t("dashx.buyOnlineD")}</p>
                    </button>
                    <button data-testid="big-have-goods" onClick={() => navigate("/goods")}
                      className="text-left border-2 border-border bg-card p-6 hover:border-accent transition-colors">
                      <Boxes className="h-7 w-7 text-accent" />
                      <div className="mt-2 font-heading font-extrabold text-lg">{t("dashx.haveGoods")}</div>
                      <p className="mt-1 text-sm text-muted-foreground">{t("dashx.haveGoodsD")}</p>
                    </button>
                  </div>
                </div>

                {/* Aperçu — colonne droite sur desktop, passe SOUS le contenu principal sur mobile */}
                <div className="mt-8 md:mt-0 space-y-4" data-testid="home-preview-col">
                  <div className="border border-border bg-card p-4">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-heading font-bold text-sm">Mes expéditions</h3>
                      <button data-testid="preview-all-shipments" className="shrink-0 whitespace-nowrap text-xs font-semibold text-accent hover:underline" onClick={() => go("shipments")}>Voir toutes</button>
                    </div>
                    <div className="mt-2 space-y-1.5">
                      {goodsShips.slice(0, 3).map((s) => (
                        <button key={s.id} onClick={() => navigate(`/shipments/${s.id}`)} className="w-full text-left border border-border p-2 text-xs hover:border-accent transition-colors">
                          <span className="font-mono font-semibold">{s.reference}</span>
                          <Badge className={`ml-2 rounded-sm text-[10px] ${clientShipStatus(s).cls}`}>{clientShipStatus(s).label}</Badge>
                        </button>
                      ))}
                      {goodsShips.length === 0 && <p className="text-xs text-muted-foreground">Aucune expédition.</p>}
                    </div>
                  </div>
                  {isOn("shop_and_ship") && (
                    <div className="border border-border bg-card p-4">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-heading font-bold text-sm">Mes achats en ligne</h3>
                        <button data-testid="preview-all-purchases" className="shrink-0 whitespace-nowrap text-xs font-semibold text-accent hover:underline" onClick={() => go("purchases")}>Voir toutes</button>
                      </div>
                      <div className="mt-2 space-y-1.5">
                        {purchases.filter((p) => p.origin !== "goods").slice(0, 3).map((p) => (
                          <div key={p.id} className="border border-border p-2 text-xs">
                            <span className="font-semibold">{p.shop_name}</span>
                            <span className="text-muted-foreground"> · {p.status === "received" ? "Colis reçu au Hub" : p.status === "in_transit" ? "En route" : "Annoncé"}</span>
                          </div>
                        ))}
                        {purchases.filter((p) => p.origin !== "goods").length === 0 && <p className="text-xs text-muted-foreground">Aucun achat en ligne.</p>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* ACHATS — demandes provenant de « Je veux acheter en ligne » */}
            {isOn("shop_and_ship") && (
              <TabsContent value="purchases" className="mt-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h1 className="font-heading font-extrabold text-2xl tracking-tight">Achats en ligne</h1>
                  <Button data-testid="purchases-new-btn" size="sm" className="rounded-sm bg-accent text-white gap-1.5" onClick={() => navigate("/shopship")}>
                    <Plus className="h-4 w-4" /> Nouvel achat en ligne
                  </Button>
                </div>
                <h3 className="mt-4 font-heading font-bold text-lg flex items-center gap-2" data-testid="purchases-list-title">
                  <ShoppingBag className="h-5 w-5 text-accent" /> Mes achats en ligne
                </h3>
                <div className="mt-2 flex gap-2" data-testid="purchases-subtabs">
                  {[["todo", "À faire"], ["active", "En cours"], ["done", "Terminés"]].map(([v, l]) => (
                    <button key={v} data-testid={`purch-sub-${v}`} onClick={() => setPurchFilter(v)}
                      className={`px-3 py-1.5 text-sm font-semibold border transition-colors ${purchFilter === v ? "bg-primary text-white border-primary" : "bg-card border-border hover:border-accent"}`}>
                      {l}
                    </button>
                  ))}
                </div>
                <div className="mt-4"><MyPurchases filter={purchFilter} embedded /></div>
              </TabsContent>
            )}
            {isOn("shop_and_ship") && (
              <TabsContent value="addresses" className="mt-4"><MyAddresses /></TabsContent>
            )}

            {/* EXPÉDITIONS — « J'ai déjà ma marchandise » + suivi jusqu'à la livraison */}
            <TabsContent value="shipments" className="mt-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h1 className="font-heading font-extrabold text-2xl tracking-tight">Expéditions</h1>
                <Button data-testid="goods-announce-btn" size="sm" className="rounded-sm bg-accent text-white gap-1.5" onClick={() => navigate("/goods")}>
                  <PackagePlus className="h-4 w-4" /> Nouvelle expédition
                </Button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="shipments-subtabs">
                {[["quote", `En attente de devis${shipQuote.length ? ` (${shipQuote.length})` : ""}`], ["todo", `À faire${shipTodo.length ? ` (${shipTodo.length})` : ""}`], ["hub", "Au Hub"], ["route", "En route"], ["done", "Terminées"]].map(([v, l]) => (
                  <button key={v} data-testid={`ship-sub-${v}`} onClick={() => go("shipments", v)}
                    className={`px-3 py-1.5 text-sm font-semibold border transition-colors ${shipSub === v ? "bg-primary text-white border-primary" : "bg-card border-border hover:border-accent"}`}>
                    {l}
                  </button>
                ))}
              </div>

              <div className="mt-4 space-y-3">
                {shipSub === "quote" && (
                  <>
                    {shipQuote.map((s) => <QuoteCard key={s.id} s={s} navigate={navigate} />)}
                    {shipQuote.length === 0 && <Empty msg="Aucune expédition en attente de devis." />}
                  </>
                )}
                {shipSub === "todo" && (
                  <>
                    {shipTodo.map((s) => <ShipCard key={s.id} s={s} t={t} navigate={navigate} action={todoOf(s)} onDone={load} />)}
                    {shipTodo.length === 0 && <Empty msg="Aucune action requise pour le moment." />}
                  </>
                )}
                {shipSub === "hub" && (
                  <>
                    {shipWaiting.length > 0 && (
                      <>
                        <h3 className="font-heading font-bold text-sm text-muted-foreground uppercase tracking-wide">En attente de réception au Hub</h3>
                        {shipWaiting.map((s) => <ShipCard key={s.id} s={s} t={t} navigate={navigate} action={todoOf(s)} onDone={load} cancellable />)}
                      </>
                    )}
                    {shipAtHub.length > 0 && (
                      <>
                        <h3 className={`font-heading font-bold text-sm text-muted-foreground uppercase tracking-wide ${shipWaiting.length ? "pt-2" : ""}`}>Reçues au Hub Colis2Go</h3>
                        {shipAtHub.map((s) => <ShipCard key={s.id} s={s} t={t} navigate={navigate} action={todoOf(s)} onDone={load} />)}
                      </>
                    )}
                    {shipWaiting.length === 0 && shipAtHub.length === 0 && <Empty msg="Aucune marchandise au Hub pour le moment." />}
                  </>
                )}
                {shipSub === "route" && (
                  <>
                    {shipRoute.map((s) => <ShipCard key={s.id} s={s} t={t} navigate={navigate} action={todoOf(s)} onDone={load} />)}
                    {shipRoute.length === 0 && <Empty msg="Aucune expédition en route." />}
                  </>
                )}
                {shipSub === "done" && (
                  <>
                    {shipDone.map((s) => <ShipCard key={s.id} s={s} t={t} navigate={navigate} action={null} onDone={load} />)}
                    {shipDone.length === 0 && <Empty msg="Aucune expédition terminée." />}
                  </>
                )}
              </div>

              {/* HISTORIQUE DES PAIEMENTS — factures indépendantes du moyen de paiement */}
              {invoices.length > 0 && (
                <>
                  <h3 className="mt-8 font-heading font-bold">{t("inv.history")} ({invoices.length})</h3>
                  <div className="mt-2 border border-border bg-card overflow-x-auto" data-testid="invoice-history">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                          <th className="px-3 py-2">{t("inv.number")}</th>
                          <th className="px-3 py-2">{t("inv.ref")}</th>
                          <th className="px-3 py-2">{t("inv.amount")}</th>
                          <th className="px-3 py-2">{t("inv.date")}</th>
                          <th className="px-3 py-2">{t("inv.status")}</th>
                          <th className="px-3 py-2">Reçu</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((inv) => {
                          const [lblK, cls] = {
                            pending: ["inv.pending", "bg-amber-100 text-amber-700"],
                            paid: ["inv.paid", "bg-emerald-600 text-white"],
                            refunded: ["inv.refunded", "bg-blue-100 text-blue-700"],
                            cancelled: ["inv.cancelled", "bg-muted text-muted-foreground"],
                          }[inv.status] || [inv.status, "bg-secondary text-primary"];
                          return (
                            <tr key={inv.id} data-testid={`invoice-row-${inv.number}`} className="border-b border-border last:border-0">
                              <td className="px-3 py-2 font-mono font-semibold">{inv.number}</td>
                              <td className="px-3 py-2 font-mono">{inv.reference}</td>
                              <td className="px-3 py-2 font-bold">{inv.amount} {curSym(inv.currency)}</td>
                              <td className="px-3 py-2 text-muted-foreground">{(inv.paid_at || inv.created_at || "").slice(0, 10)}</td>
                              <td className="px-3 py-2"><Badge className={`rounded-sm ${cls}`} data-testid={`invoice-status-${inv.number}`}>{t(lblK)}</Badge></td>
                              <td className="px-3 py-2">
                                {inv.status === "paid" && (
                                  <Button size="sm" variant="outline" data-testid={`receipt-btn-${inv.number}`} className="rounded-sm h-7 gap-1 text-xs"
                                    onClick={() => downloadPdf(`/invoices/${inv.id}/receipt`, `recu-${inv.number}.pdf`)}>
                                    <Download className="h-3.5 w-3.5" /> PDF
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </TabsContent>

            {/* PROFIL / COMPTE */}
            <TabsContent value="profile" className="mt-4">
              <h1 className="font-heading font-extrabold text-2xl tracking-tight mb-4">Profil</h1>
              <ProfileEditor user={user} setUser={setUser} disputes={disputes} t={t} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

const Empty = ({ msg }) => <p className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">{msg}</p>;

// Carte « Sur devis » : pas de montant, pas de facture, pas de paiement tant qu'aucun prix n'existe
function QuoteCard({ s, navigate }) {
  const Mode = s.mode === "air" ? Plane : Ship;
  const nbColis = (s.packages || []).reduce((a, p) => a + (p.cartons || 0), 0) || (s.declared_items || []).reduce((a, it) => a + (Number(it.qty) || 1), 0);
  return (
    <div data-testid={`quote-card-${s.reference}`} className="border border-border bg-card p-4 hover:border-accent transition-colors">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-heading font-bold">{s.reference}</span>
            <Badge className={`rounded-sm ${s.quote_status === "sent" && s.quote?.amount != null ? "bg-accent text-white" : "bg-amber-100 text-amber-700"}`}>
              {s.quote_status === "sent" && s.quote?.amount != null ? "Devis reçu — décision requise" : "En attente de devis"}
            </Badge>
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-sm text-primary font-medium">
            <Mode className="h-4 w-4 text-accent" /> {s.goods_country || s.origin_city} <ArrowRight className="h-4 w-4" /> {s.dest_city || s.dest_country}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
            {s.goods_description && <span className="truncate max-w-[260px]">{s.goods_description}</span>}
            {nbColis > 0 && <span>{nbColis} colis</span>}
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">Le prix sera communiqué après réception et vérification de votre marchandise au Hub Colis2Go.</p>
        </div>
        <Button size="sm" variant="outline" data-testid={`quote-details-${s.reference}`} className="rounded-sm text-xs" onClick={() => navigate(`/shipments/${s.id}`)}>Voir les détails</Button>
      </div>
    </div>
  );
}

// Carte expédition — référence, origine → destination, contenu, statut, montant, prochaine action
function ShipCard({ s, t, navigate, action, onDone, cancellable = false }) {
  const Mode = s.mode === "air" ? Plane : Ship;
  const pkgs = s.packages || [];
  const weight = pkgs.reduce((a, p) => a + (p.weight_kg || 0), 0);
  const { label, cls } = clientShipStatus(s);
  const nbColis = pkgs.reduce((a, p) => a + (p.cartons || 0), 0) || (s.declared_items || []).reduce((a, it) => a + (Number(it.qty) || 1), 0);
  return (
    <div data-testid={`shipment-card-${s.reference}`} className="border border-border bg-card p-4 hover:border-accent transition-colors">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-heading font-bold">{s.reference}</span>
            <Badge className={`rounded-sm ${cls}`}>{label}</Badge>
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-sm text-primary font-medium">
            <Mode className="h-4 w-4 text-accent" /> {s.goods_country || s.origin_city} <ArrowRight className="h-4 w-4" /> {s.dest_city || s.dest_country}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
            {s.goods_description && <span className="truncate max-w-[260px]">{s.goods_description}</span>}
            {nbColis > 0 && <span>{nbColis} colis</span>}
            {weight > 0 && <span>{Math.round(weight * 10) / 10} kg</span>}
            {s.final_price != null && <span className="text-accent font-bold">{s.final_price} {curOf(s)}</span>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {action && (
            <Button size="sm" data-testid={`ship-action-${s.reference}`} className="rounded-sm bg-accent hover:bg-accent/90 text-white text-xs" onClick={action.onClick}>
              {action.btn}{action.amount ? ` — ${action.amount}` : ""}
            </Button>
          )}
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" data-testid={`ship-details-${s.reference}`} className="rounded-sm text-xs" onClick={() => navigate(`/shipments/${s.id}`)}>{t("ssx.details")}</Button>
            <a href={wa(`Bonjour Colis2Go, j'ai une question concernant mon expédition ${s.reference}.`)} target="_blank" rel="noreferrer">
              <Button size="sm" variant="outline" className="rounded-sm text-xs" data-testid={`ship-question-${s.reference}`}>{t("q.parcel")}</Button>
            </a>
            {cancellable && s.status === "created" && <CancelAction kind="shipment" id={s.id} refLabel={s.reference} onDone={onDone} t={t} />}
          </div>
        </div>
      </div>
      {["arrived", "available"].includes(s.status) && <PickupBox s={s} />}
    </div>
  );
}

function ProfileEditor({ user, setUser, disputes = [], t }) {
  const [f, setF] = useState({ name: user?.name || "", language: user?.language || "fr" });
  const [addresses, setAddresses] = useState(user?.addresses || []);
  const [addr, setAddr] = useState({ label: "", country: "", city: "", address: "", phone: "" });
  const [waEdit, setWaEdit] = useState(false);
  const [waVal, setWaVal] = useState(user?.whatsapp || "");
  const save = async () => {
    const { data } = await api.put("/me", { ...f, addresses });
    if (setUser) setUser(data);
    toast.success("Profil enregistré");
  };
  const saveWa = async () => {
    const v = waVal.trim();
    if (!/^\+?[0-9 ]{8,18}$/.test(v)) { toast.error("Numéro invalide — utilisez l'indicatif pays, ex. +44 7XXXXXXXXX"); return; }
    const { data } = await api.put("/me", { whatsapp: v.startsWith("+") ? v : `+${v}` });
    if (setUser) setUser(data);
    setWaEdit(false);
    toast.success("Numéro WhatsApp enregistré");
  };
  const addAddress = () => {
    if (!addr.label || !addr.city) { toast.error("Libellé et ville requis"); return; }
    setAddresses([...addresses, { ...addr, id: Date.now().toString() }]);
    setAddr({ label: "", country: "", city: "", address: "", phone: "" });
  };
  return (
    <div className="max-w-2xl space-y-5">
      <div className="border border-border bg-card p-5 space-y-3">
        <h3 className="font-heading font-bold">Informations personnelles</h3>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Nom complet</Label><Input data-testid="profile-name" className="rounded-sm" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><Label>Email</Label><Input data-testid="profile-email" className="rounded-sm bg-secondary/50" value={user?.email || ""} readOnly /></div>
        </div>
        {/* WHATSAPP — numéro principal du compte, réutilisé pour tous les dossiers */}
        <div className="border-2 border-[#25D366]/40 bg-[#25D366]/5 p-3.5" data-testid="profile-whatsapp-block">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <MessageCircle className="h-3.5 w-3.5 text-[#25D366]" /> WhatsApp
              </div>
              {waEdit ? (
                <Input data-testid="profile-whatsapp-input" className="rounded-sm mt-1 w-56" placeholder="+44 7XXXXXXXXX" value={waVal} onChange={(e) => setWaVal(e.target.value)} />
              ) : (
                <div className={`mt-0.5 font-semibold ${user?.whatsapp ? "" : "text-muted-foreground italic"}`} data-testid="profile-whatsapp-value">
                  {user?.whatsapp || "Non renseigné"}
                </div>
              )}
            </div>
            {waEdit ? (
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => { setWaEdit(false); setWaVal(user?.whatsapp || ""); }}>Annuler</Button>
                <Button size="sm" data-testid="profile-whatsapp-save" className="rounded-sm bg-accent text-white" onClick={saveWa}>Enregistrer</Button>
              </div>
            ) : (
              <Button size="sm" data-testid="profile-whatsapp-edit" variant="outline" className="rounded-sm" onClick={() => setWaEdit(true)}>
                {user?.whatsapp ? "Modifier" : "Ajouter mon WhatsApp"}
              </Button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Langue préférée</Label>
            <Select value={f.language} onValueChange={(v) => setF({ ...f, language: v })}>
              <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="fr">Français</SelectItem><SelectItem value="en">English</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
      </div>
      <div className="border border-border bg-card p-5 space-y-3">
        <h3 className="font-heading font-bold">Mes adresses Colis2Go</h3>
        {addresses.map((a, i) => (
          <div key={a.id || i} className="flex items-center justify-between border border-border p-2.5 text-sm">
            <span><strong>{a.label}</strong> — {[a.address, a.city, a.country].filter(Boolean).join(", ")} {a.phone && `· ${a.phone}`}</span>
            <Button size="icon" variant="ghost" className="text-destructive h-8 w-8" onClick={() => setAddresses(addresses.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <div className="grid grid-cols-2 gap-2">
          <Input data-testid="addr-label" className="rounded-sm" placeholder={t("prof.addrLabel")} value={addr.label} onChange={(e) => setAddr({ ...addr, label: e.target.value })} />
          <Input className="rounded-sm" placeholder="Pays" value={addr.country} onChange={(e) => setAddr({ ...addr, country: e.target.value })} />
          <Input data-testid="addr-city" className="rounded-sm" placeholder="Ville" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} />
          <Input className="rounded-sm" placeholder="Téléphone lié à cette adresse" value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} />
          <Input className="rounded-sm col-span-2" placeholder="Adresse" value={addr.address} onChange={(e) => setAddr({ ...addr, address: e.target.value })} />
        </div>
        <Button data-testid="addr-add" size="sm" variant="outline" className="rounded-sm gap-1" onClick={addAddress}><Plus className="h-4 w-4" /> Ajouter l’adresse</Button>
      </div>
      <Button data-testid="profile-save" className="rounded-sm bg-accent text-white" onClick={save}>Enregistrer le profil</Button>
      {disputes.length > 0 && (
        <div className="border border-border bg-card p-5">
          <h3 className="font-heading font-bold">Mes litiges ({disputes.length})</h3>
          <div className="mt-2 space-y-2">
            {disputes.map((d) => (
              <div key={d.id} data-testid={`dispute-${d.id}`} className="border border-border p-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="font-semibold text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-accent" /> {d.reference} · {DISPUTE_TYPES[d.type] || d.type}</span>
                  <Badge className={`rounded-sm ${d.status === "open" ? "bg-amber-100 text-amber-700" : "bg-emerald-600 text-white"}`}>{d.status === "open" ? "Ouvert" : "Résolu"}</Badge>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{d.description}</p>
                {d.resolution && <p className="mt-1 text-sm"><strong>Résolution :</strong> {d.resolution}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
      <SecurityPanel />
    </div>
  );
}
